// BusinessDayPage.tsx
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { apiClient } from '../lib/api'
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpDown,
  Banknote,
  BarChart2,
  BarChart3,
  Building,
  Calculator,
  Calendar,
  CheckSquare,
  ChevronLeft,
  ClipboardList,
  Clock,
  Coins,
  CreditCard,
  Database,
  DollarSign,
  Download,
  Eye,
  FileBarChart,
  FileDigit,
  FileText,
  FileText as FileTextIcon,
  Filter,
  HandCoins,
  Lock,
  Maximize,
  Package,
  PiggyBank,
  PlayCircle,
  Plus,
  Printer,
  Receipt,
  RefreshCcw,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  StopCircle,
  Unlock,
  Users,
  Wallet
} from 'lucide-react'
import toast from 'react-hot-toast'

// Custom Components
import { ErrorBoundary } from '../components/ui/ErrorBoundary'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { DataTable, Column } from '../components/ui/DataTable'

// Types based on schema.ts
interface BusinessDay {
  id: string
  cash_register_id?: string | null
  opened_by?: string | null
  closed_by?: string | null
  shift_number?: number | null
  z_report_number?: string | null
  opened_at?: string | null
  closed_at?: string | null
  starting_cash?: number | null
  expected_cash?: number | null
  actual_cash?: number | null
  difference?: number | null
  cash_difference?: number | null
  gross_sales?: number | null
  total_refunds?: number | null
  total_discounts?: number | null
  total_tax?: number | null
  net_sales?: number | null
  tips_collected?: number | null
  service_charges?: number | null
  paid_in_total?: number | null
  paid_out_total?: number | null
  safe_drops_total?: number | null
  bank_deposit_amount?: number | null
  voided_count?: number | null
  x_report_count?: number | null
  transaction_count?: number | null
  status?: string | null
  notes?: string | null
  created_at: string
  updated_at: string
  total_voided?: number | null
  total_tips?: number | null
  total_service_charges?: number | null
  drawer_open_count?: number | null
  cash_deposits?: number | null
  cash_withdrawals?: number | null
  approved_by?: string | null
  approved_at?: string | null
  fiscal_z_number?: string | null
  fiscal_z_printed?: boolean | null
  total_cost?: number | null
  gross_profit?: number | null
  labor_hours?: number | null
  labor_cost?: number | null
  last_x_report_time?: string | null
  loan_amount?: number | null
  payout_amount?: number | null
  initial_coin?: number | null
  initial_bills?: number | null
  expected_coin?: number | null
  expected_bills?: number | null
  actual_coin?: number | null
  actual_bills?: number | null
  drawer_count?: number | null
  currency_breakdown_json?: any | null
  variance_notes?: string | null
  variance_resolved?: boolean | null
  variance_resolved_by?: string | null
  variance_resolved_at?: string | null
  safe_drop_count?: number | null
  total_safe_drops?: number | null
  paid_in_count?: number | null
  total_paid_in?: number | null
  paid_out_count?: number | null
  total_paid_out?: number | null
  no_sale_count?: number | null
  largest_transaction_amount?: number | null
  smallest_transaction_amount?: number | null

  // Relations
  cash_register?: {
    id: string
    name: string
    code: string
    branch_id: string
  }
  opened_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  closed_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  approved_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  variance_resolved_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
}

interface BusinessDayPayment {
  id: string
  business_day_id: string
  payment_type_id: string
  expected_amount?: number | null
  difference?: number | null
  created_at: string

  payment_type?: {
    id: string
    name: string
    code: string
    is_active: boolean
  }
  business_day?: BusinessDay
}

interface CashDenomination {
  id: string
  business_day_id?: string | null
  cash_drawer_operation_id?: string | null
  denomination_type?: string | null
  denomination_value?: number | null
  quantity?: number | null
  total_amount?: number | null
  count_type?: string | null
  counted_by?: string | null
  counted_at?: string | null
  created_at: string

  counted_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
}

interface CashDrawerOperation {
  id: string
  business_day_id?: string | null
  cash_register_id?: string | null
  operation_type?: string | null
  amount?: number | null
  reason?: string | null
  reference_number?: string | null
  performed_by?: string | null
  authorized_by?: string | null
  performed_at?: string | null
  notes?: string | null
  created_at: string

  performed_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  authorized_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
}

interface CashMovement {
  id: string
  cash_register_id?: string | null
  business_day_id?: string | null
  user_id?: string | null
  movement_type?: string | null
  amount?: number | null
  reason?: string | null
  reference_number?: string | null
  approved_by?: string | null
  approved_at?: string | null
  movement_time?: string | null
  notes?: string | null
  created_at: string

  user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  approved_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
}

interface CashRegisterSession {
  id: string
  cash_register_id: string
  user_id: string
  business_day_id?: string | null
  session_number?: number | null
  opened_at: string
  cloased_at?: string | null
  opening_cash?: number | null
  closing_cash?: number | null
  expected_cash?: number | null
  actual_cash?: number | null
  variance_amount?: number | null
  cash_sales?: number | null
  card_sales?: number | null
  other_sales?: number | null
  total_sales?: number | null
  total_refunds?: number | null
  total_voids?: number | null
  transaction_count?: number | null
  notes?: string | null
  is_active: boolean
  created_at: string
  updated_at: string

  user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
  business_day?: BusinessDay
}

interface SafeDrop {
  id: string
  business_day_id?: string | null
  cash_register_id?: string | null
  drop_number?: number | null
  drop_amount: number
  drop_type?: string | null
  bag_number?: string | null
  performed_by?: string | null
  witnessed_by?: string | null
  dropped_at: string
  collected_by?: string | null
  collected_at?: string | null
  verified_by?: string | null
  verified_at?: string | null
  verified_amount?: number | null
  variance_amount?: number | null
  notes?: string | null
  created_at: string

  performed_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  witnessed_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  collected_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  verified_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
}

interface PettyCash {
  id: string
  store_id: string
  transaction_number: string
  transaction_type: string
  amount: number
  category?: string | null
  description?: string | null
  receipt_number?: string | null
  recipient?: string | null
  performed_by?: string | null
  approved_by?: string | null
  transaction_date: string
  created_at: string

  performed_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  approved_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  store?: {
    id: string
    name: string
    code: string
  }
}

interface ShiftChange {
  id: string
  cash_register_id: string
  business_day_id?: string | null
  from_user_id: string
  to_user_id: string
  change_time: string
  cash_amount_declared?: number | null
  cash_amount_counted?: number | null
  variance_amount?: number | null
  variance_reason_id?: string | null
  transaction_count_from_user?: number | null
  sales_total_from_user?: number | null
  witnessed_by?: string | null
  notes?: string | null
  is_verified: boolean
  created_at: string

  from_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  to_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  witnessed_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
  variance_reason?: {
    id: string
    reason_code: string
    reason_name: string
  }
}

interface ShiftReport {
  id: string
  z_report_id?: string | null
  business_day_id?: string | null
  user_id: string
  cash_register_id?: string | null
  clock_in_time: string
  clock_out_time?: string | null
  total_sales?: number | null
  total_transactions?: number | null
  total_items_sold?: number | null
  created_at: string

  user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
  z_report?: {
    id: string
    report_number: number
  }
}

interface ZReport {
  id: string
  report_number: number
  business_day_id: string
  cash_register_id: string
  opened_by: string
  closed_by?: string | null
  opened_at: string
  closed_at?: string | null
  starting_cash?: number | null
  total_sales?: number | null
  total_cash?: number | null
  total_card?: number | null
  total_other?: number | null
  total_tax?: number | null
  total_discount?: number | null
  expected_cash?: number | null
  actual_cash?: number | null
  cash_difference?: number | null
  total_transactions?: number | null
  total_items_sold?: number | null
  total_voided_items?: number | null
  total_refunds?: number | null
  notes?: string | null
  created_at: string

  opened_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  closed_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
}

interface FiscalDailyReport {
  id: string
  report_number: number
  z_number?: string | null
  cash_register_id: string
  business_day_id?: string | null
  report_date: string
  opening_amount?: number | null
  closing_amount?: number | null
  total_sales?: number | null
  total_tax?: number | null
  total_voided?: number | null
  total_refunds?: number | null
  fiscal_signature?: string | null
  generated_by?: string | null
  created_at: string

  generated_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
}

interface TenderDeclaration {
  id: string
  business_day_id?: string | null
  payment_type_id?: string | null
  expected_amount?: number | null
  declared_amount?: number | null
  variance_amount?: number | null
  variance_reason?: string | null
  declared_by?: string | null
  declared_at: string
  approved_by?: string | null
  approved_at?: string | null
  notes?: string | null

  declared_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  approved_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  payment_type?: {
    id: string
    name: string
    code: string
  }
}

interface TillFloatDeclaration {
  id: string
  cash_register_id?: string | null
  business_day_id?: string | null
  user_id?: string | null
  declaration_type?: string | null
  declaration_time: string
  notes_100?: number | null
  notes_50?: number | null
  notes_20?: number | null
  notes_10?: number | null
  notes_5?: number | null
  notes_1?: number | null
  coins_1?: number | null
  coins_050?: number | null
  coins_025?: number | null
  coins_010?: number | null
  coins_005?: number | null
  coins_001?: number | null
  total_declared?: number | null
  total_expected?: number | null
  variance_amount?: number | null
  variance_reason_id?: string | null
  verified_by?: string | null
  verified_at?: string | null
  notes?: string | null
  created_at: string

  user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  verified_by_user?: {
    id: string
    username: string
    first_name?: string
    last_name?: string
  }
  variance_reason?: {
    id: string
    reason_code: string
    reason_name: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
}

interface UserClockIn {
  id: string
  employee_id: string
  clock_in_time: string
  clock_out_time?: string | null
  store_id?: string | null
  cash_register_id?: string | null
  total_hours?: number | null
  break_minutes?: number | null
  notes?: string | null
  created_at: string
  updated_at: string

  employee?: {
    id: string
    employee_number: string
    user_id?: string | null
    user?: {
      id: string
      username: string
      first_name?: string
      last_name?: string
    }
  }
  store?: {
    id: string
    name: string
    code: string
  }
  cash_register?: {
    id: string
    name: string
    code: string
  }
}

interface BusinessDayAnalytics {
  totalBusinessDays: number
  openBusinessDays: number
  closedBusinessDays: number
  totalSales: number
  totalCashVariance: number
  averageSales: number
  averageVariance: number
  totalTransactions: number
  totalRefunds: number
  totalDiscounts: number
  totalTips: number
  averageTransactionValue: number
  largestTransaction: number
  netSales: number
  grossProfit: number
  totalCost: number
  laborCost: number
  safeDropsTotal: number
  bankDepositsTotal: number
  paidInsTotal: number
  paidOutsTotal: number
  pettyCashTotal: number
  voidedAmountTotal: number
  serviceChargesTotal: number
  taxCollected: number
  cashTransactions: number
  cardTransactions: number
  otherTransactions: number
  cashAmount: number
  cardAmount: number
  otherAmount: number
  checkAmount: number
  giftCardAmount: number
  creditAmount: number
}

type TabType =
  | 'business-days'
  | 'payments'
  | 'cash-operations'
  | 'cash-movements'
  | 'denominations'
  | 'tender-declarations'
  | 'float-declarations'
  | 'safe-drops'
  | 'petty-cash'
  | 'sessions'
  | 'clock-ins'
  | 'shift-changes'
  | 'shift-reports'
  | 'fiscal-reports'
  | 'z-reports'
  | 'analytics'
  | 'voids-refunds'
  | 'bank-deposits'

type BusinessDaySubTab = 'all' | 'open' | 'closed' | 'today' | 'week' | 'month' | 'has-variance' | 'needs-approval'
type CashOpSubTab = 'all' | 'loan' | 'payout' | 'pickup' | 'safe-drop' | 'bank-deposit' | 'petty-cash'
type FiscalSubTab = 'z-reports' | 'shift-reports' | 'daily-reports' | 'fiscal-documents'

const ITEMS_PER_PAGE = 50
const DEBOUNCE_DELAY = 300

export default function BusinessDayPage() {
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<TabType>('business-days')
  const [activeSubTab, setActiveSubTab] = useState<any>('all')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmDialogProps, setConfirmDialogProps] = useState<any>({})

  // Data States
  const [businessDays, setBusinessDays] = useState<BusinessDay[]>([])
  const [businessDayPayments, setBusinessDayPayments] = useState<BusinessDayPayment[]>([])
  const [cashDrawerOperations, setCashDrawerOperations] = useState<CashDrawerOperation[]>([])
  const [cashMovements, setCashMovements] = useState<CashMovement[]>([])
  const [cashDenominations, setCashDenominations] = useState<CashDenomination[]>([])
  const [tenderDeclarations, setTenderDeclarations] = useState<TenderDeclaration[]>([])
  const [tillFloatDeclarations, setTillFloatDeclarations] = useState<TillFloatDeclaration[]>([])
  const [safeDrops, setSafeDrops] = useState<SafeDrop[]>([])
  const [pettyCash, setPettyCash] = useState<PettyCash[]>([])
  const [cashRegisterSessions, setCashRegisterSessions] = useState<CashRegisterSession[]>([])
  const [userClockIns, setUserClockIns] = useState<UserClockIn[]>([])
  const [shiftChanges, setShiftChanges] = useState<ShiftChange[]>([])
  const [shiftReports, setShiftReports] = useState<ShiftReport[]>([])
  const [zReports, setZReports] = useState<ZReport[]>([])
  const [fiscalDailyReports, setFiscalDailyReports] = useState<FiscalDailyReport[]>([])
  const [analytics, setAnalytics] = useState<BusinessDayAnalytics>({
    totalBusinessDays: 0,
    openBusinessDays: 0,
    closedBusinessDays: 0,
    totalSales: 0,
    totalCashVariance: 0,
    averageSales: 0,
    averageVariance: 0,
    totalTransactions: 0,
    totalRefunds: 0,
    totalDiscounts: 0,
    totalTips: 0,
    averageTransactionValue: 0,
    largestTransaction: 0,
    netSales: 0,
    grossProfit: 0,
    totalCost: 0,
    laborCost: 0,
    safeDropsTotal: 0,
    bankDepositsTotal: 0,
    paidInsTotal: 0,
    paidOutsTotal: 0,
    pettyCashTotal: 0,
    voidedAmountTotal: 0,
    serviceChargesTotal: 0,
    taxCollected: 0,
    cashTransactions: 0,
    cardTransactions: 0,
    otherTransactions: 0,
    cashAmount: 0,
    cardAmount: 0,
    otherAmount: 0,
    checkAmount: 0,
    giftCardAmount: 0,
    creditAmount: 0
  })

  // UI State
  const [searchTerm, setSearchTerm] = useState('')
  const [filterOptions, setFilterOptions] = useState<any>({
    status: '',
    cash_register_id: '',
    opened_by: '',
    date_from: '',
    date_to: '',
    has_variance: '',
    needs_approval: '',
    shift_number: '',
    z_report_number: '',
    store_id: ''
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedBusinessDay, setSelectedBusinessDay] = useState<BusinessDay | null>(null)
  const [showCashCountDialog, setShowCashCountDialog] = useState(false)
  const [countType, setCountType] = useState<'opening' | 'closing'>('opening')

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        fetchData()
      }
    }, DEBOUNCE_DELAY)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch data on tab/subtab change
  useEffect(() => {
    fetchData()
  }, [activeTab, activeSubTab, currentPage, filterOptions])

  /**
   * Main data fetching orchestrator with error handling
   */
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      switch (activeTab) {
        case 'business-days':
          await fetchBusinessDaysData()
          break
        case 'payments':
          await fetchPaymentsData()
          break
        case 'cash-operations':
          await fetchCashOperationsData()
          break
        case 'cash-movements':
          await fetchCashMovementsData()
          break
        case 'denominations':
          await fetchDenominationsData()
          break
        case 'tender-declarations':
          await fetchTenderDeclarationsData()
          break
        case 'float-declarations':
          await fetchTillFloatDeclarationsData()
          break
        case 'safe-drops':
          await fetchSafeDropsData()
          break
        case 'petty-cash':
          await fetchPettyCashData()
          break
        case 'sessions':
          await fetchSessionsData()
          break
        case 'clock-ins':
          await fetchClockInsData()
          break
        case 'shift-changes':
          await fetchShiftChangesData()
          break
        case 'shift-reports':
          await fetchShiftReportsData()
          break
        case 'z-reports':
          await fetchZReportsData()
          break
        case 'fiscal-reports':
          await fetchFiscalReportsData()
          break
        case 'analytics':
          await fetchAnalyticsData()
          break
        case 'voids-refunds':
          await fetchVoidsRefundsData()
          break
        case 'bank-deposits':
          await fetchBankDepositsData()
          break
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data'
      setError(errorMessage)
      toast.error(errorMessage)
      console.error('Error fetching data:', err)
    } finally {
      setLoading(false)
    }
  }, [activeTab, activeSubTab, currentPage, searchTerm, filterOptions])

  const fetchBusinessDaysData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
      ...filterOptions
    })

    if (activeSubTab === 'open') {
      params.append('status', 'open')
    } else if (activeSubTab === 'closed') {
      params.append('status', 'closed')
    } else if (activeSubTab === 'today') {
      const today = new Date().toISOString().split('T')[0]
      params.append('date_from', today)
      params.append('date_to', today + 'T23:59:59')
    } else if (activeSubTab === 'week') {
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)
      params.append('date_from', weekAgo.toISOString())
    } else if (activeSubTab === 'month') {
      const monthAgo = new Date()
      monthAgo.setMonth(monthAgo.getMonth() - 1)
      params.append('date_from', monthAgo.toISOString())
    } else if (activeSubTab === 'has-variance') {
      params.append('has_variance', 'true')
    } else if (activeSubTab === 'needs-approval') {
      params.append('needs_approval', 'true')
    }

    const response = await apiClient.get(`/api/business-days?${params}`)
    setBusinessDays(response.data.data || response.data)
    setTotalCount(response.data.total || response.data.length || 0)
  }

  const fetchPaymentsData = async () => {
    const response = await apiClient.get('/api/business-days/payments')
    setBusinessDayPayments(response.data)
  }

  const fetchCashOperationsData = async () => {
    const params = new URLSearchParams()
    if (activeSubTab !== 'all') {
      params.append('operation_type', activeSubTab)
    }
    const response = await apiClient.get(`/api/business-days/cash-operations?${params}`)
    setCashDrawerOperations(response.data)
  }

  const fetchCashMovementsData = async () => {
    const response = await apiClient.get('/api/business-days/cash-movements')
    setCashMovements(response.data)
  }

  const fetchDenominationsData = async () => {
    const response = await apiClient.get('/api/business-days/denominations')
    setCashDenominations(response.data)
  }

  const fetchTenderDeclarationsData = async () => {
    const response = await apiClient.get('/api/business-days/tender-declarations')
    setTenderDeclarations(response.data)
  }

  const fetchTillFloatDeclarationsData = async () => {
    const response = await apiClient.get('/api/business-days/till-float-declarations')
    setTillFloatDeclarations(response.data)
  }

  const fetchSafeDropsData = async () => {
    const response = await apiClient.get('/api/business-days/safe-drops')
    setSafeDrops(response.data)
  }

  const fetchPettyCashData = async () => {
    const response = await apiClient.get('/api/business-days/petty-cash')
    setPettyCash(response.data)
  }

  const fetchSessionsData = async () => {
    const response = await apiClient.get('/api/business-days/sessions')
    setCashRegisterSessions(response.data)
  }

  const fetchClockInsData = async () => {
    const response = await apiClient.get('/api/business-days/clock-ins')
    setUserClockIns(response.data)
  }

  const fetchShiftChangesData = async () => {
    const response = await apiClient.get('/api/business-days/shift-changes')
    setShiftChanges(response.data)
  }

  const fetchShiftReportsData = async () => {
    const response = await apiClient.get('/api/business-days/shift-reports')
    setShiftReports(response.data)
  }

  const fetchZReportsData = async () => {
    const response = await apiClient.get('/api/business-days/z-reports')
    setZReports(response.data)
  }

  const fetchFiscalReportsData = async () => {
    if (activeSubTab === 'z-reports') {
      await fetchZReportsData()
    } else if (activeSubTab === 'shift-reports') {
      await fetchShiftReportsData()
    } else if (activeSubTab === 'daily-reports') {
      const response = await apiClient.get('/api/business-days/fiscal-daily-reports')
      setFiscalDailyReports(response.data)
    }
  }

  const fetchAnalyticsData = async () => {
    const response = await apiClient.get('/api/business-days/analytics')
    setAnalytics(response.data)
  }

  const fetchVoidsRefundsData = async () => {
    // Implementation for voids and refunds
    console.log('Fetching voids and refunds data')
  }

  const fetchBankDepositsData = async () => {
    // Implementation for bank deposits
    console.log('Fetching bank deposits data')
  }

  const handleOpenBusinessDay = async (data: any) => {
    try {
      await apiClient.post('/api/business-days/open', data)
      toast.success('Business day opened successfully')
      fetchBusinessDaysData()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to open business day')
    }
  }

  const handleCloseBusinessDay = async (id: string, data: any) => {
    try {
      await apiClient.post(`/api/business-days/${id}/close`, data)
      toast.success('Business day closed successfully')
      fetchBusinessDaysData()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to close business day')
    }
  }

  const handleApproveBusinessDay = async (id: string) => {
    try {
      await apiClient.post(`/api/business-days/${id}/approve`)
      toast.success('Business day approved successfully')
      fetchBusinessDaysData()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to approve business day')
    }
  }

  const handleResolveVariance = async (id: string, data: any) => {
    try {
      await apiClient.post(`/api/business-days/${id}/resolve-variance`, data)
      toast.success('Variance resolved successfully')
      fetchBusinessDaysData()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to resolve variance')
    }
  }

  const handleMaximize = () => {
    setIsFullscreen(!isFullscreen)
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const openConfirmDialog = (props: any) => {
    setConfirmDialogProps(props)
    setShowConfirmDialog(true)
  }

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false)
    setConfirmDialogProps({})
  }

  const confirmAction = () => {
    if (confirmDialogProps.onConfirm) {
      confirmDialogProps.onConfirm()
    }
    closeConfirmDialog()
  }

  /**
   * Business Day Columns
   */
  const businessDayColumns: Column<BusinessDay>[] = useMemo(() => [
    {
      key: 'opened_at',
      header: 'Date',
      accessor: (row) => row.opened_at || row.created_at,
      sortable: true,
      filterable: true,
      width: '120px',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    {
      key: 'cash_register',
      header: 'Register',
      accessor: (row) => row.cash_register?.name || '-',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div>
          <div className="text-white font-medium">{value}</div>
          <div className="text-xs text-gray-500">{row.cash_register?.code || '-'}</div>
        </div>
      )
    },
    {
      key: 'shift_number',
      header: 'Shift',
      accessor: (row) => row.shift_number || '-',
      width: '80px',
      align: 'center'
    },
    {
      key: 'opened_by',
      header: 'Opened By',
      accessor: (row) => row.opened_by_user 
        ? `${row.opened_by_user.first_name || ''} ${row.opened_by_user.last_name || ''}`.trim() || row.opened_by_user.username
        : '-',
      width: '140px'
    },
    {
      key: 'gross_sales',
      header: 'Sales',
      accessor: (row) => row.gross_sales,
      align: 'right',
      render: (value) => <span className="text-green-400 font-semibold">${(value || 0).toFixed(2)}</span>,
      sortable: true
    },
    {
      key: 'net_sales',
      header: 'Net Sales',
      accessor: (row) => row.net_sales,
      align: 'right',
      render: (value) => <span className="text-blue-400 font-semibold">${(value || 0).toFixed(2)}</span>,
      sortable: true
    },
    {
      key: 'transaction_count',
      header: 'Trans',
      accessor: (row) => row.transaction_count,
      align: 'right',
      width: '80px',
      sortable: true
    },
    {
      key: 'difference',
      header: 'Variance',
      accessor: (row) => row.difference,
      align: 'right',
      render: (value) => {
        const variance = value || 0
        return (
          <span className={`font-bold ${
            Math.abs(variance) < 0.01 ? 'text-green-400' :
            variance > 0 ? 'text-yellow-400' : 'text-red-400'
          }`}>
            ${Math.abs(variance).toFixed(2)}
          </span>
        )
      },
      sortable: true
    },
    {
      key: 'gross_profit',
      header: 'Profit',
      accessor: (row) => row.gross_profit,
      align: 'right',
      render: (value) => <span className="text-purple-400 font-semibold">${(value || 0).toFixed(2)}</span>,
      sortable: true
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.status,
      align: 'center',
      render: (value) => {
        if (value === 'open') {
          return <span className="px-2 py-1 text-xs rounded-full bg-green-900 text-green-200">Open</span>
        } else if (value === 'closed') {
          return <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-200">Closed</span>
        } else if (value === 'pending_approval') {
          return <span className="px-2 py-1 text-xs rounded-full bg-yellow-900 text-yellow-200">Pending Approval</span>
        } else if (value === 'approved') {
          return <span className="px-2 py-1 text-xs rounded-full bg-purple-900 text-purple-200">Approved</span>
        } else {
          return <span className="px-2 py-1 text-xs rounded-full bg-gray-700 text-gray-400">{value || 'Unknown'}</span>
        }
      },
      sortable: true
    }
  ], [])

  /**
   * Cash Operations Columns
   */
  const cashOperationsColumns: Column<CashDrawerOperation>[] = useMemo(() => [
    {
      key: 'performed_at',
      header: 'Date/Time',
      accessor: (row) => row.performed_at || row.created_at,
      sortable: true,
      render: (value) => (
        <div>
          <div className="text-white">{value ? new Date(value).toLocaleDateString() : '-'}</div>
          <div className="text-xs text-gray-500">{value ? new Date(value).toLocaleTimeString() : '-'}</div>
        </div>
      )
    },
    {
      key: 'operation_type',
      header: 'Type',
      accessor: (row) => row.operation_type,
      sortable: true,
      render: (value) => {
        const type = value || 'unknown'
        const config: Record<string, { bg: string, text: string, label: string }> = {
          'loan': { bg: 'bg-blue-900', text: 'text-blue-200', label: 'Loan' },
          'payout': { bg: 'bg-red-900', text: 'text-red-200', label: 'Payout' },
          'pickup': { bg: 'bg-green-900', text: 'text-green-200', label: 'Pickup' },
          'safe_drop': { bg: 'bg-purple-900', text: 'text-purple-200', label: 'Safe Drop' },
          'bank_deposit': { bg: 'bg-indigo-900', text: 'text-indigo-200', label: 'Bank Deposit' },
          'petty_cash': { bg: 'bg-amber-900', text: 'text-amber-200', label: 'Petty Cash' },
          'cash_in': { bg: 'bg-emerald-900', text: 'text-emerald-200', label: 'Cash In' },
          'cash_out': { bg: 'bg-rose-900', text: 'text-rose-200', label: 'Cash Out' }
        }
        const cfg = config[type] || { bg: 'bg-gray-700', text: 'text-gray-400', label: type }
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${cfg.bg} ${cfg.text}`}>
            {cfg.label}
          </span>
        )
      }
    },
    {
      key: 'amount',
      header: 'Amount',
      accessor: (row) => row.amount,
      align: 'right',
      render: (value, row) => {
        const amount = value || 0
        const isPositive = ['loan', 'pickup', 'cash_in', 'safe_drop'].includes(row.operation_type || '')
        return (
          <span className={`font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {isPositive ? '+' : '-'}${amount.toFixed(2)}
          </span>
        )
      },
      sortable: true
    },
    {
      key: 'performed_by',
      header: 'Performed By',
      accessor: (row) => row.performed_by_user
        ? `${row.performed_by_user.first_name || ''} ${row.performed_by_user.last_name || ''}`.trim() || row.performed_by_user.username
        : '-'
    },
    {
      key: 'authorized_by',
      header: 'Authorized By',
      accessor: (row) => row.authorized_by_user
        ? `${row.authorized_by_user.first_name || ''} ${row.authorized_by_user.last_name || ''}`.trim() || row.authorized_by_user.username
        : '-'
    },
    {
      key: 'reason',
      header: 'Reason',
      accessor: (row) => row.reason || '-',
      render: (value) => <span className="text-sm text-gray-400">{value}</span>
    },
    {
      key: 'reference_number',
      header: 'Reference',
      accessor: (row) => row.reference_number || '-',
      className: 'font-mono text-xs'
    }
  ], [])

  /**
   * Safe Drops Columns
   */
  const safeDropsColumns: Column<SafeDrop>[] = useMemo(() => [
    {
      key: 'dropped_at',
      header: 'Date/Time',
      accessor: (row) => row.dropped_at,
      sortable: true,
      render: (value) => (
        <div>
          <div className="text-white">{new Date(value).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500">{new Date(value).toLocaleTimeString()}</div>
        </div>
      )
    },
    {
      key: 'drop_amount',
      header: 'Amount',
      accessor: (row) => row.drop_amount,
      align: 'right',
      render: (value) => <span className="text-green-400 font-bold">${(value || 0).toFixed(2)}</span>,
      sortable: true
    },
    {
      key: 'drop_number',
      header: 'Drop #',
      accessor: (row) => row.drop_number || '-',
      className: 'font-mono',
      align: 'center'
    },
    {
      key: 'bag_number',
      header: 'Bag #',
      accessor: (row) => row.bag_number || '-',
      className: 'font-mono'
    },
    {
      key: 'performed_by',
      header: 'Performed By',
      accessor: (row) => row.performed_by_user
        ? `${row.performed_by_user.first_name || ''} ${row.performed_by_user.last_name || ''}`.trim() || row.performed_by_user.username
        : '-'
    },
    {
      key: 'witnessed_by',
      header: 'Witnessed By',
      accessor: (row) => row.witnessed_by_user
        ? `${row.witnessed_by_user.first_name || ''} ${row.witnessed_by_user.last_name || ''}`.trim() || row.witnessed_by_user.username
        : '-'
    },
    {
      key: 'verified_amount',
      header: 'Verified',
      accessor: (row) => row.verified_amount,
      align: 'right',
      render: (value) => value ? <span className="text-blue-400">${value.toFixed(2)}</span> : '-'
    },
    {
      key: 'variance_amount',
      header: 'Variance',
      accessor: (row) => row.variance_amount,
      align: 'right',
      render: (value) => {
        if (!value) return '-'
        return (
          <span className={`font-bold ${Math.abs(value) < 0.01 ? 'text-green-400' : 'text-red-400'}`}>
            ${Math.abs(value).toFixed(2)}
          </span>
        )
      }
    }
  ], [])

  /**
   * Petty Cash Columns
   */
  const pettyCashColumns: Column<PettyCash>[] = useMemo(() => [
    {
      key: 'transaction_date',
      header: 'Date/Time',
      accessor: (row) => row.transaction_date,
      sortable: true,
      render: (value) => (
        <div>
          <div className="text-white">{new Date(value).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500">{new Date(value).toLocaleTimeString()}</div>
        </div>
      )
    },
    {
      key: 'transaction_type',
      header: 'Type',
      accessor: (row) => row.transaction_type,
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'in' ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'
        }`}>
          {value === 'in' ? 'In' : 'Out'}
        </span>
      )
    },
    {
      key: 'amount',
      header: 'Amount',
      accessor: (row) => row.amount,
      align: 'right',
      render: (value, row) => (
        <span className={`font-bold ${row.transaction_type === 'in' ? 'text-green-400' : 'text-red-400'}`}>
          {row.transaction_type === 'in' ? '+' : '-'}${(value || 0).toFixed(2)}
        </span>
      ),
      sortable: true
    },
    {
      key: 'category',
      header: 'Category',
      accessor: (row) => row.category || '-'
    },
    {
      key: 'description',
      header: 'Description',
      accessor: (row) => row.description || '-',
      render: (value) => <span className="text-sm text-gray-400">{value}</span>
    },
    {
      key: 'receipt_number',
      header: 'Receipt #',
      accessor: (row) => row.receipt_number || '-',
      className: 'font-mono text-xs'
    },
    {
      key: 'recipient',
      header: 'Recipient',
      accessor: (row) => row.recipient || '-'
    },
    {
      key: 'performed_by',
      header: 'Performed By',
      accessor: (row) => row.performed_by_user
        ? `${row.performed_by_user.first_name || ''} ${row.performed_by_user.last_name || ''}`.trim() || row.performed_by_user.username
        : '-'
    },
    {
      key: 'approved_by',
      header: 'Approved By',
      accessor: (row) => row.approved_by_user
        ? `${row.approved_by_user.first_name || ''} ${row.approved_by_user.last_name || ''}`.trim() || row.approved_by_user.username
        : '-'
    }
  ], [])

  const renderContent = () => {
    switch (activeTab) {
      case 'business-days':
        return renderBusinessDaysContent()
      case 'payments':
        return renderPaymentsContent()
      case 'cash-operations':
        return renderCashOperationsContent()
      case 'cash-movements':
        return renderCashMovementsContent()
      case 'denominations':
        return renderDenominationsContent()
      case 'tender-declarations':
        return renderTenderDeclarationsContent()
      case 'float-declarations':
        return renderFloatDeclarationsContent()
      case 'safe-drops':
        return renderSafeDropsContent()
      case 'petty-cash':
        return renderPettyCashContent()
      case 'sessions':
        return renderSessionsContent()
      case 'clock-ins':
        return renderClockInsContent()
      case 'shift-changes':
        return renderShiftChangesContent()
      case 'shift-reports':
        return renderShiftReportsContent()
      case 'z-reports':
        return renderZReportsContent()
      case 'fiscal-reports':
        return renderFiscalReportsContent()
      case 'analytics':
        return renderAnalyticsContent()
      case 'voids-refunds':
        return renderVoidsRefundsContent()
      case 'bank-deposits':
        return renderBankDepositsContent()
      default:
        return null
    }
  }

  const renderBusinessDaysContent = () => {
    const subTabs = [
      { id: 'all', label: 'All', icon: Database },
      { id: 'open', label: 'Open', icon: Unlock },
      { id: 'closed', label: 'Closed', icon: Lock },
      { id: 'today', label: 'Today', icon: Calendar },
      { id: 'week', label: 'This Week', icon: Calendar },
      { id: 'month', label: 'This Month', icon: Calendar },
      { id: 'has-variance', label: 'Has Variance', icon: AlertTriangle },
      { id: 'needs-approval', label: 'Needs Approval', icon: ShieldAlert }
    ]

    return (
      <div className="space-y-4">
        {/* Sub Tabs */}
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Advanced Filters - Maintained same design as requested */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Filter size={18} />
              Advanced Filters
            </h3>
            <button
              onClick={() => setFilterOptions({
                status: '',
                cash_register_id: '',
                opened_by: '',
                date_from: '',
                date_to: '',
                has_variance: '',
                needs_approval: '',
                shift_number: '',
                z_report_number: '',
                store_id: ''
              })}
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Status</label>
              <select
                value={filterOptions.status}
                onChange={(e) => setFilterOptions({...filterOptions, status: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">From Date</label>
              <input
                type="date"
                value={filterOptions.date_from}
                onChange={(e) => setFilterOptions({...filterOptions, date_from: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">To Date</label>
              <input
                type="date"
                value={filterOptions.date_to}
                onChange={(e) => setFilterOptions({...filterOptions, date_to: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Variance</label>
              <select
                value={filterOptions.has_variance}
                onChange={(e) => setFilterOptions({...filterOptions, has_variance: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              >
                <option value="">All</option>
                <option value="yes">With Variance</option>
                <option value="no">No Variance</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Shift #</label>
              <input
                type="number"
                value={filterOptions.shift_number}
                onChange={(e) => setFilterOptions({...filterOptions, shift_number: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                placeholder="Shift number"
              />
            </div>
          </div>
        </div>

        {/* Search & Actions */}
        <div className="flex justify-between items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search business days..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 w-80"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => toast.success('Export started')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Download size={18} />
              Export
            </button>
            <button
              onClick={() => openConfirmDialog({
                title: 'Open Business Day',
                message: 'Start a new business day?',
                onConfirm: () => {
                  // Open business day dialog
                  toast.success('Open business day dialog')
                }
              })}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <PlayCircle size={18} />
              Open Business Day
            </button>
          </div>
        </div>

        {/* Business Days Table */}
        <DataTable
          data={businessDays}
          columns={businessDayColumns}
          loading={loading}
          error={error}
          pageSize={ITEMS_PER_PAGE}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          rowKey={(row) => row.id}
          actions={(row) => (
            <div className="flex items-center justify-center gap-2">
              <button
                className="p-1 hover:bg-gray-700 rounded"
                title="View Details"
                onClick={() => setSelectedBusinessDay(row)}
              >
                <Eye size={16} className="text-blue-400" />
              </button>
              {row.status === 'open' && (
                <>
                  <button
                    className="p-1 hover:bg-gray-700 rounded"
                    title="Count Cash"
                    onClick={() => {
                      setSelectedBusinessDay(row)
                      setCountType('closing')
                      setShowCashCountDialog(true)
                    }}
                  >
                    <Calculator size={16} className="text-yellow-400" />
                  </button>
                  <button
                    className="p-1 hover:bg-gray-700 rounded"
                    title="Close Business Day"
                    onClick={() => openConfirmDialog({
                      title: 'Close Business Day',
                      message: 'Close this business day and generate Z-Report?',
                      onConfirm: () => handleCloseBusinessDay(row.id, {})
                    })}
                  >
                    <StopCircle size={16} className="text-red-400" />
                  </button>
                </>
              )}
              {row.status === 'closed' && !row.approved_by && (
                <button
                  className="p-1 hover:bg-gray-700 rounded"
                  title="Approve Business Day"
                  onClick={() => openConfirmDialog({
                    title: 'Approve Business Day',
                    message: 'Approve this closed business day?',
                    onConfirm: () => handleApproveBusinessDay(row.id)
                  })}
                >
                  <CheckSquare size={16} className="text-green-400" />
                </button>
              )}
              {row.variance_resolved === false && (
                <button
                  className="p-1 hover:bg-gray-700 rounded"
                  title="Resolve Variance"
                  onClick={() => openConfirmDialog({
                    title: 'Resolve Variance',
                    message: 'Mark variance as resolved?',
                    onConfirm: () => handleResolveVariance(row.id, {})
                  })}
                >
                  <ShieldCheck size={16} className="text-purple-400" />
                </button>
              )}
              {row.status === 'closed' && (
                <button className="p-1 hover:bg-gray-700 rounded" title="Print Z-Report">
                  <Printer size={16} className="text-gray-400" />
                </button>
              )}
            </div>
          )}
          emptyState={
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <Calendar size={48} className="mx-auto text-gray-600 mb-4" />
              <div className="text-gray-400 text-lg mb-2">No business days found</div>
              <div className="text-gray-500 text-sm mb-4">Open a business day to start tracking sales</div>
              <button
                onClick={() => openConfirmDialog({
                  title: 'Open Business Day',
                  message: 'Start a new business day?',
                  onConfirm: () => toast.success('Open business day')
                })}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <PlayCircle size={18} className="inline mr-2" />
                Open Business Day
              </button>
            </div>
          }
        />
      </div>
    )
  }

  const renderPaymentsContent = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Payment Type Totals per Business Day</h3>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Business Day</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Payment Type</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Expected Amount</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Difference</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {businessDayPayments.map(payment => (
              <tr key={payment.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-gray-400">
                  {payment.business_day?.opened_at 
                    ? new Date(payment.business_day.opened_at).toLocaleDateString() 
                    : payment.business_day_id}
                </td>
                <td className="px-4 py-3 text-sm text-white">{payment.payment_type?.name || '-'}</td>
                <td className="px-4 py-3 text-sm text-right text-blue-400">
                  ${(payment.expected_amount || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={`font-bold ${Math.abs(payment.difference || 0) < 0.01 ? 'text-green-400' : 'text-red-400'}`}>
                    ${Math.abs(payment.difference || 0).toFixed(2)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderCashOperationsContent = () => {
    const subTabs = [
      { id: 'all', label: 'All Operations', icon: DollarSign },
      { id: 'loan', label: 'Loans', icon: Banknote },
      { id: 'payout', label: 'Payouts', icon: Wallet },
      { id: 'pickup', label: 'Pickups', icon: Package },
      { id: 'safe_drop', label: 'Safe Drops', icon: PiggyBank },
      { id: 'bank_deposit', label: 'Bank Deposits', icon: Building },
      { id: 'petty_cash', label: 'Petty Cash', icon: HandCoins }
    ]

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        <DataTable
          data={cashDrawerOperations}
          columns={cashOperationsColumns}
          loading={loading}
          error={error}
          pageSize={ITEMS_PER_PAGE}
          totalCount={cashDrawerOperations.length}
          onPageChange={setCurrentPage}
          rowKey={(row) => row.id}
          emptyState={
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <DollarSign size={48} className="mx-auto text-gray-600 mb-4" />
              <div className="text-gray-400 text-lg">No cash operations found</div>
            </div>
          }
        />
      </div>
    )
  }

  const renderCashMovementsContent = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Cash Movements</h3>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Date/Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Approved By</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {cashMovements.map(movement => (
              <tr key={movement.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-gray-400">
                  {movement.movement_time 
                    ? new Date(movement.movement_time).toLocaleString() 
                    : new Date(movement.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    movement.movement_type === 'in' ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'
                  }`}>
                    {movement.movement_type || 'unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-bold">
                  <span className={movement.movement_type === 'in' ? 'text-green-400' : 'text-red-400'}>
                    ${(movement.amount || 0).toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-white">
                  {movement.user 
                    ? `${movement.user.first_name || ''} ${movement.user.last_name || ''}`.trim() || movement.user.username
                    : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {movement.approved_by_user 
                    ? `${movement.approved_by_user.first_name || ''} ${movement.approved_by_user.last_name || ''}`.trim() || movement.approved_by_user.username
                    : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">{movement.reason || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderDenominationsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-white font-semibold mb-4">Cash Denominations</h3>
        <div className="space-y-3">
          {cashDenominations.map(denom => (
            <div key={denom.id} className="bg-gray-750 rounded p-4 flex items-center justify-between">
              <div>
                <div className="text-white font-medium">
                  {denom.denomination_type === 'bill' ? 'Bill' : 'Coin'}: ${denom.denomination_value}
                </div>
                <div className="text-xs text-gray-500">
                  Count: {denom.quantity} × Total: ${denom.total_amount?.toFixed(2) || '0.00'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  denom.count_type === 'opening' ? 'bg-blue-900 text-blue-200' : 'bg-green-900 text-green-200'
                }`}>
                  {denom.count_type || 'unknown'}
                </span>
                <span className="text-xs text-gray-500">
                  {denom.counted_at ? new Date(denom.counted_at).toLocaleTimeString() : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderTenderDeclarationsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Payment Type</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Expected</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Declared</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Variance</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Declared By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {tenderDeclarations.map(tender => (
              <tr key={tender.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(tender.declared_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-sm text-white">{tender.payment_type?.name || '-'}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-300">
                  ${(tender.expected_amount || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-white">
                  ${(tender.declared_amount || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={`font-bold ${
                    Math.abs(tender.variance_amount || 0) < 0.01 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    ${Math.abs(tender.variance_amount || 0).toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {tender.declared_by_user 
                    ? `${tender.declared_by_user.first_name || ''} ${tender.declared_by_user.last_name || ''}`.trim() || tender.declared_by_user.username
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderFloatDeclarationsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Date/Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">User</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Expected</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Declared</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Variance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {tillFloatDeclarations.map(declaration => (
              <tr key={declaration.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(declaration.declaration_time).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    declaration.declaration_type === 'opening' ? 'bg-blue-900 text-blue-200' : 'bg-green-900 text-green-200'
                  }`}>
                    {declaration.declaration_type || 'unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-white">
                  {declaration.user 
                    ? `${declaration.user.first_name || ''} ${declaration.user.last_name || ''}`.trim() || declaration.user.username
                    : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right text-blue-400">
                  ${(declaration.total_expected || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-white">
                  ${(declaration.total_declared || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={`font-bold ${
                    Math.abs(declaration.variance_amount || 0) < 0.01 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    ${Math.abs(declaration.variance_amount || 0).toFixed(2)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderSafeDropsContent = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Safe Deposits</h3>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <Plus size={18} />
          New Safe Drop
        </button>
      </div>

      <DataTable
        data={safeDrops}
        columns={safeDropsColumns}
        loading={loading}
        error={error}
        pageSize={ITEMS_PER_PAGE}
        totalCount={safeDrops.length}
        onPageChange={setCurrentPage}
        rowKey={(row) => row.id}
        emptyState={
          <div className="bg-gray-800 rounded-lg p-12 text-center">
            <PiggyBank size={48} className="mx-auto text-gray-600 mb-4" />
            <div className="text-gray-400 text-lg">No safe drops recorded</div>
          </div>
        }
      />
    </div>
  )

  const renderPettyCashContent = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Petty Cash Transactions</h3>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <Plus size={18} />
          New Transaction
        </button>
      </div>

      <DataTable
        data={pettyCash}
        columns={pettyCashColumns}
        loading={loading}
        error={error}
        pageSize={ITEMS_PER_PAGE}
        totalCount={pettyCash.length}
        onPageChange={setCurrentPage}
        rowKey={(row) => row.id}
        emptyState={
          <div className="bg-gray-800 rounded-lg p-12 text-center">
            <Wallet size={48} className="mx-auto text-gray-600 mb-4" />
            <div className="text-gray-400 text-lg">No petty cash transactions</div>
          </div>
        }
      />
    </div>
  )

  const renderSessionsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Register</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Login</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Logout</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Cash Sales</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Total Sales</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Variance</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {cashRegisterSessions.map(session => (
              <tr key={session.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-white">
                  {session.user 
                    ? `${session.user.first_name || ''} ${session.user.last_name || ''}`.trim() || session.user.username
                    : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {session.cash_register?.code || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(session.opened_at).toLocaleTimeString()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {session.cloased_at ? new Date(session.cloased_at).toLocaleTimeString() : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right text-green-400">
                  ${(session.cash_sales || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-blue-400">
                  ${(session.total_sales || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={`font-bold ${
                    Math.abs(session.variance_amount || 0) < 0.01 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    ${Math.abs(session.variance_amount || 0).toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    session.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                  }`}>
                    {session.is_active ? 'Active' : 'Closed'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderClockInsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Employee</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Clock In</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Clock Out</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Hours</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Break (min)</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Store</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {userClockIns.map(clockIn => (
              <tr key={clockIn.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-white">
                  {clockIn.employee?.user 
                    ? `${clockIn.employee.user.first_name || ''} ${clockIn.employee.user.last_name || ''}`.trim() || clockIn.employee.user.username
                    : clockIn.employee?.employee_number || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(clockIn.clock_in_time).toLocaleTimeString()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {clockIn.clock_out_time ? new Date(clockIn.clock_out_time).toLocaleTimeString() : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right text-white">
                  {clockIn.total_hours ? clockIn.total_hours.toFixed(2) : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-300">{clockIn.break_minutes || 0}</td>
                <td className="px-4 py-3 text-sm text-gray-400">{clockIn.store?.code || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderShiftChangesContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Register</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Outgoing</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Incoming</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Cash Counted</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Variance</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Witnessed By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {shiftChanges.map(change => (
              <tr key={change.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(change.change_time).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {change.cash_register?.code || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-white">
                  {change.from_user 
                    ? `${change.from_user.first_name || ''} ${change.from_user.last_name || ''}`.trim() || change.from_user.username
                    : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-white">
                  {change.to_user 
                    ? `${change.to_user.first_name || ''} ${change.to_user.last_name || ''}`.trim() || change.to_user.username
                    : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right text-green-400">
                  ${(change.cash_amount_counted || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={`font-bold ${
                    Math.abs(change.variance_amount || 0) < 0.01 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    ${Math.abs(change.variance_amount || 0).toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {change.witnessed_by_user 
                    ? `${change.witnessed_by_user.first_name || ''} ${change.witnessed_by_user.last_name || ''}`.trim() || change.witnessed_by_user.username
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderShiftReportsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Employee</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Clock In</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Clock Out</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Sales</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Transactions</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Items Sold</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {shiftReports.map(report => (
              <tr key={report.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-white">
                  {report.user 
                    ? `${report.user.first_name || ''} ${report.user.last_name || ''}`.trim() || report.user.username
                    : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(report.clock_in_time).toLocaleTimeString()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {report.clock_out_time ? new Date(report.clock_out_time).toLocaleTimeString() : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right text-green-400">
                  ${(report.total_sales || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-300">{report.total_transactions || 0}</td>
                <td className="px-4 py-3 text-sm text-right text-blue-400">{report.total_items_sold || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderZReportsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Register</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Sales</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Cash</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Card</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Variance</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {zReports.map(report => (
              <tr key={report.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-gray-300 font-mono">{report.report_number}</td>
                <td className="px-4 py-3 text-sm text-white">
                  {new Date(report.opened_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">{report.cash_register?.code || '-'}</td>
                <td className="px-4 py-3 text-sm text-right text-green-400">
                  ${(report.total_sales || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-blue-400">
                  ${(report.total_cash || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-purple-400">
                  ${(report.total_card || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={(report.cash_difference || 0) === 0 
                    ? 'text-green-400' : 'text-red-400'}>
                    ${Math.abs(report.cash_difference || 0).toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button className="p-1 hover:bg-gray-700 rounded">
                    <Printer size={16} className="text-blue-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderFiscalReportsContent = () => {
    const subTabs = [
      { id: 'z-reports', label: 'Z-Reports', icon: FileText },
      { id: 'shift-reports', label: 'Shift Reports', icon: Users },
      { id: 'daily-reports', label: 'Daily Reports', icon: Calendar },
      { id: 'fiscal-documents', label: 'Fiscal Documents', icon: FileTextIcon }
    ]

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {activeSubTab === 'z-reports' && renderZReportsContent()}
        {activeSubTab === 'shift-reports' && renderShiftReportsContent()}
        {activeSubTab === 'daily-reports' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Z-Number</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Opening</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Closing</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Sales</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Tax</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {fiscalDailyReports.map(report => (
                  <tr key={report.id} className="hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm text-gray-300">{report.report_number}</td>
                    <td className="px-4 py-3 text-sm text-white">
                      {new Date(report.report_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400 font-mono">{report.z_number || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-400">
                      ${(report.opening_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-400">
                      ${(report.closing_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-400">
                      ${(report.total_sales || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-orange-400">
                      ${(report.total_tax || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {activeSubTab === 'fiscal-documents' && (
          <div className="text-gray-400">Fiscal documents content</div>
        )}
      </div>
    )
  }

  const renderAnalyticsContent = () => (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Total Business Days</span>
            <Calendar className="text-blue-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.totalBusinessDays}</div>
          <div className="text-xs text-gray-500 mt-1">
            Open: {analytics.openBusinessDays} | Closed: {analytics.closedBusinessDays}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Total Sales</span>
            <DollarSign className="text-green-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">
            ${analytics.totalSales.toFixed(2)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Net: ${analytics.netSales.toFixed(2)} | Gross Profit: ${analytics.grossProfit.toFixed(2)}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Transactions</span>
            <ShoppingCart className="text-purple-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.totalTransactions}</div>
          <div className="text-xs text-gray-500 mt-1">
            Avg: ${analytics.averageTransactionValue.toFixed(2)}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Cash Variance</span>
            <AlertTriangle className="text-orange-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">
            ${Math.abs(analytics.totalCashVariance).toFixed(2)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Avg: ${Math.abs(analytics.averageVariance).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Payment Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm">Payment Methods</span>
            <CreditCard className="text-blue-400" size={20} />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Cash</span>
              <span className="text-green-400 font-semibold">${analytics.cashAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Card</span>
              <span className="text-blue-400 font-semibold">${analytics.cardAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Other</span>
              <span className="text-purple-400 font-semibold">${analytics.otherAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm">Financial Summary</span>
            <BarChart2 className="text-green-400" size={20} />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Tax Collected</span>
              <span className="text-orange-400 font-semibold">${analytics.taxCollected.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Tips</span>
              <span className="text-yellow-400 font-semibold">${analytics.totalTips.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Discounts</span>
              <span className="text-red-400 font-semibold">${analytics.totalDiscounts.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm">Cash Management</span>
            <PiggyBank className="text-indigo-400" size={20} />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Safe Drops</span>
              <span className="text-green-400 font-semibold">${analytics.safeDropsTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Bank Deposits</span>
              <span className="text-blue-400 font-semibold">${analytics.bankDepositsTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Petty Cash</span>
              <span className="text-amber-400 font-semibold">${analytics.pettyCashTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderVoidsRefundsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-white font-semibold mb-4">Voids & Refunds Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-750 rounded p-4">
            <div className="text-gray-400 text-sm mb-1">Total Voided Amount</div>
            <div className="text-2xl font-bold text-red-400">${analytics.voidedAmountTotal.toFixed(2)}</div>
          </div>
          <div className="bg-gray-750 rounded p-4">
            <div className="text-gray-400 text-sm mb-1">Total Refunds</div>
            <div className="text-2xl font-bold text-orange-400">${analytics.totalRefunds.toFixed(2)}</div>
          </div>
          <div className="bg-gray-750 rounded p-4">
            <div className="text-gray-400 text-sm mb-1">Service Charges</div>
            <div className="text-2xl font-bold text-yellow-400">${analytics.serviceChargesTotal.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderBankDepositsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-white font-semibold mb-4">Bank Deposits Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-750 rounded p-4">
            <div className="text-gray-400 text-sm mb-1">Total Bank Deposits</div>
            <div className="text-2xl font-bold text-green-400">${analytics.bankDepositsTotal.toFixed(2)}</div>
          </div>
          <div className="bg-gray-750 rounded p-4">
            <div className="text-gray-400 text-sm mb-1">Paid Ins</div>
            <div className="text-2xl font-bold text-blue-400">${analytics.paidInsTotal.toFixed(2)}</div>
          </div>
          <div className="bg-gray-750 rounded p-4">
            <div className="text-gray-400 text-sm mb-1">Paid Outs</div>
            <div className="text-2xl font-bold text-red-400">${analytics.paidOutsTotal.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-900">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-3 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center gap-4">
            <Link to="/app" className="text-gray-400 hover:text-white transition-colors">
              <ChevronLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <Calendar size={22} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Business Day Management</h2>
                <div className="text-sm text-gray-500">Track business days, cash operations, and fiscal reports</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              className="p-2 hover:bg-gray-700 rounded"
              onClick={fetchData}
              title="Refresh"
            >
              <RefreshCw size={18} className="text-gray-400" />
            </button>
            <button onClick={handleMaximize} className="p-2 hover:bg-gray-700 rounded" title="Fullscreen">
              <Maximize size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="bg-gray-800 border-b border-gray-700">
          <div className="flex gap-1 px-6 overflow-x-auto">
            {[
              { id: 'business-days', label: 'Business Days', icon: Calendar, description: 'Daily operations' },
              { id: 'payments', label: 'Payments', icon: CreditCard, description: 'Payment records' },
              { id: 'cash-operations', label: 'Cash Operations', icon: DollarSign, description: 'Register actions' },
              { id: 'cash-movements', label: 'Cash Movements', icon: ArrowUpDown, description: 'Money transfers' },
              { id: 'denominations', label: 'Denominations', icon: Coins, description: 'Cash count' },
              { id: 'tender-declarations', label: 'Tender Declarations', icon: ClipboardList, description: 'Amount verification' },
              { id: 'float-declarations', label: 'Float Declarations', icon: Banknote, description: 'Starting cash' },
              { id: 'safe-drops', label: 'Safe Drops', icon: PiggyBank, description: 'Vault deposits' },
              { id: 'petty-cash', label: 'Petty Cash', icon: HandCoins, description: 'Small expenses' },
              { id: 'sessions', label: 'Sessions', icon: Users, description: 'User shifts' },
              { id: 'clock-ins', label: 'Clock Ins', icon: Clock, description: 'Time tracking' },
              { id: 'shift-changes', label: 'Shift Changes', icon: RefreshCcw, description: 'Handover logs' },
              { id: 'shift-reports', label: 'Shift Reports', icon: FileText, description: 'Individual reports' },
              { id: 'z-reports', label: 'Z-Reports', icon: FileDigit, description: 'Daily totals' },
              { id: 'fiscal-reports', label: 'Fiscal Reports', icon: FileBarChart, description: 'Tax compliance' },
              { id: 'analytics', label: 'Analytics', icon: BarChart3, description: 'Business insights' },
              { id: 'voids-refunds', label: 'Voids & Refunds', icon: AlertCircle, description: 'Error tracking' },
              { id: 'bank-deposits', label: 'Bank Deposits', icon: Building, description: 'External deposits' }
            ].map(tab => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as TabType)
                    setActiveSubTab(
                      tab.id === 'business-days' ? 'all' :
                      tab.id === 'cash-operations' ? 'all' :
                      tab.id === 'fiscal-reports' ? 'z-reports' : 'all'
                    )
                    setCurrentPage(1)
                  }}
                  className={`group flex flex-col items-start px-4 py-3 border-b-2 transition-all whitespace-nowrap min-w-[120px] ${
                    isActive
                      ? 'border-blue-500 text-blue-400 bg-gray-750'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={18} />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </div>
                  <div className={`text-xs transition-all ${
                    isActive ? 'text-blue-300/70' : 'text-gray-500 group-hover:text-gray-400'
                  }`}>
                    {tab.description}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={confirmDialogProps.title}
        message={confirmDialogProps.message}
        onConfirm={confirmAction}
        onClose={closeConfirmDialog}
      />
    </ErrorBoundary>
  )
}